import { HStack, VStack, Text, IconArrow } from '@gazin/ecommerce-ui'
import { useEffect, useRef, useState } from 'react'
import { formatPrice } from 'src/util/formatPrice'
import { PaymentMethodsEnum } from 'src/util/enums/payment-methods'
import { CreditCardInstallment } from '../creditCardInstallment'
import { AvailablePaymentMethodProps } from '../types'

export const PaymentMethodInfo = ({
  slug,
  icon,
  name,
  description,
  installments,
  installmentsMaxQuantityWithoutInterest,
  totalInCash,
  totalInInstallments,
}: AvailablePaymentMethodProps) => {
  const [isOpenContent, setIsOpenContent] = useState(
    slug === PaymentMethodsEnum.Credit_card
  )

  const contentRef = useRef<HTMLDivElement>(null)

  const total =
    slug === PaymentMethodsEnum.Credit_card ? totalInInstallments : totalInCash

  useEffect(() => {
    if (slug === PaymentMethodsEnum.Credit_card && installments) {
      setIsOpenContent(true)
      if (contentRef.current) {
        contentRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    }
  }, [slug, installments])

  const handleToggleContent = () => {
    setIsOpenContent(!isOpenContent)
  }

  const installmentsWithoutInterest = Array.from(
    { length: installmentsMaxQuantityWithoutInterest! },
    (_, index) => {
      const installmentValue = total / (index + 1)
      return {
        amount: installmentValue,
        interestRate: 0.0,
        quantity: index + 1,
        total,
      }
    }
  )

  return (
    <VStack w="100%" ref={contentRef}>
      <HStack
        color="primary.500"
        fontWeight={600}
        fontSize={{ base: '1rem', md: '1.25rem' }}
        lineHeight={{ base: '1.4rem', md: '1.75rem' }}
        justifyContent="space-between"
        w="100%"
        alignItems="center"
        cursor="pointer"
        onClick={handleToggleContent}
      >
        <HStack alignItems="center" justifyContent="flex-start" gap="0.5rem">
          {icon}
          <Text>{name}</Text>
        </HStack>
        <IconArrow
          as="button"
          color="primary.500"
          height=".65rem"
          width=".65rem"
          cursor="pointer"
          transform={isOpenContent ? 'rotate(90deg)' : 'rotate(0deg)'}
        />
      </HStack>
      <VStack
        data-testid="payment-method-content"
        color="neutral.600"
        alignItems="flex-start"
        w="100%"
        display={isOpenContent ? 'flex' : 'none'}
      >
        {slug === PaymentMethodsEnum.Credit_card ? (
          <CreditCardInstallment
            installments={installments || installmentsWithoutInterest}
          />
        ) : (
          <>
            <Text fontSize="0.625rem" lineHeight="0.875rem">
              {description}
            </Text>
            <Text
              fontSize="0.875rem"
              lineHeight="1.225rem"
              fontWeight={700}
              color="neutral.400"
            >
              Total{' '}
              <Text as="span" color="neutral.700">
                {formatPrice.format(total)}
              </Text>
            </Text>
          </>
        )}
      </VStack>
    </VStack>
  )
}
